import React, { useState, useEffect } from "react";
import {
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBBtn,
  MDBInput,
} from "mdb-react-ui-kit";
import { useMessageBox } from "../components/Common/MessageBox";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import { storeLoginAsHandoff } from "../utils/authStorage";
import "./InviteUsersPage.css";
import {
  canManageUsers,
  canDeleteUser,
  canEditUser,
  canAddUserWithRole,
  canLoginAsUser,
  getAssignableRolesForAdd,
  normalizeRole,
} from "../utils/rolePermissions";

const USER_ROLES = [
  {
    value: "Owner",
    label: "Owner",
    description: "Full Access + Can Transfer Ownership",
    icon: "👑",
    borderColor: "#dc3545",
    ownerOnly: true,
  },
  {
    value: "Admin",
    label: "Admin",
    description: "Can Add/Remove Users, All Pages",
    icon: "🔧",
    borderColor: "#0d6efd",
  },
  {
    value: "Editor",
    label: "Editor",
    description: "Products & Dashboard Only",
    icon: "✏️",
    borderColor: "#6c757d",
  },
  {
    value: "Seller",
    label: "Seller",
    description: "Sell and manage product listings",
    icon: "🛒",
    borderColor: "#198754",
  },
  {
    value: "Seller Manager",
    label: "Seller Manager",
    description: "Manage sellers and product listings",
    icon: "📦",
    borderColor: "#6f42c1",
  },
    {
    value: "Rider",
    label: "Rider",
    description: "Pick a product from seller",
    icon: "📦",
    borderColor: "#6f42c1",
  },
      {
    value: "outbound",
    label: "outbound",
    description: "Pick a product from seller",
    icon: "📦",
    borderColor: "#6f42c1",
  },
  
];

const roleToCssClass = (role) =>
  `role-${String(role || "")
    .toLowerCase()
    .replace(/\s+/g, "-")}`;

const InviteUsersPage = () => {
  const { showInfo, showSuccess, showWarning, showError, MessageBoxComponent } =
    useMessageBox();
  const { user } = useAuth();

  // Loading state
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    firstName: "",
    lastName: "",
    role: "",
    phoneNumber: "",
    storeName: "",
    address: "",
    skPrefix: "",
    payoutPercentage: "",
  });

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    role: "",
    phoneNumber: "",
    storeName: "",
    address: "",
    skPrefix: "", // 👈 ye add karo
    payoutPercentage: "", // 👈 global seller/vendor payout %
  });

  // Users data from API
  const [users, setUsers] = useState([]);

  const currentUserRole = normalizeRole(user?.role);
  const currentUserId = user?.id;
  const userCanManageUsers = canManageUsers(currentUserRole);
  const isSellerManager = currentUserRole === "seller manager";

  const addUserRoleOptions = USER_ROLES.filter((role) =>
    getAssignableRolesForAdd(currentUserRole).includes(
      normalizeRole(role.value),
    ),
  );

  const editUserRoleOptions = isSellerManager
    ? USER_ROLES.filter((role) => role.value === "Seller")
    : USER_ROLES.filter(
        (role) => !role.ownerOnly || currentUserRole === "owner",
      );

  // Fetch all company users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      console.log("📡 FETCHING USERS from /api/users");
      const response = await api.get("/api/users");
      console.log("📥 FETCH USERS RESPONSE:", response.data);
      if (response.data.success) {
        console.log("✅ Users loaded:", response.data.users.length, "users");
        console.table(
         response.data.users.map((u) => ({
            id: u.id,
            email: u.email,
            role: u.role,
            firstName: u.firstName,
            lastName: u.lastName,
            storeName: u.storeName,
            address: u.address,
            phoneNumber: u.phoneNumber,
            skPrefix: u.sku_prefix || u.skPrefix, // backend consistency handle
          }))
        );
        setUsers(response.data.users);
      } else {
        console.warn("⚠️ FETCH returned success=false:", response.data);
      }
    } catch (error) {
      console.error("❌ FETCH USERS ERROR:", error);
      showError(error.response?.data?.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle form input changes
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Handle form submit
  const handleSubmit = async () => {
    const { firstName, lastName, email, password, role } = formData;

    // Validation
    if (!firstName || !lastName || !email || !password || !role) {
      showWarning("Please fill all fields");
      return;
    }
    if (
      role === "Seller" &&
      (!formData.phoneNumber ||
        !formData.storeName ||
        !formData.address ||
        !formData.skPrefix)
    ) {
      showWarning(
        "Phone Number, Store Name, Address and sku prefix are required for Seller",
      );
      return;
    }
    if (role === "Seller" && !formData.skPrefix) {
      showWarning("SKU Prefix is required for Seller");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showWarning("Please enter a valid email address");
      return;
    }

    // Password validation
    if (password.length < 6) {
      showWarning("Password must be at least 6 characters");
      return;
    }

    if (!canAddUserWithRole(currentUserRole, role)) {
      showError(
        isSellerManager
          ? "Seller Managers can only add users with the Seller role"
          : "You do not have permission to add this role",
      );
      return;
    }

    try {
      const response = await api.post("/api/users", formData);

      if (response.data.success) {
        // Check if multiple owners warning
        if (response.data.multipleOwners) {
          showInfo("⚠️ Company now has multiple owners");
        }

        showSuccess(response.data.message || "User added successfully!");

        // Reset form and close modal
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          password: "",
          role: "",
          phoneNumber: "",
          storeName: "",
          address: "",
          skPrefix: "",
          payoutPercentage: "",
        });
        setShowModal(false);

        // Refresh user list
        fetchUsers();
      }
    } catch (error) {
      console.error("Error adding user:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to add user";
      showError(errorMessage);
    }
  };

  // Handle delete user
  const handleDelete = async (userId, userRole) => {
    if (!canDeleteUser(currentUserRole, userRole)) {
      showError(
        isSellerManager
          ? "Seller Managers can only remove Seller users"
          : "You do not have permission to remove this user",
      );
      return;
    }

    const isSelf = userId === currentUserId;
    const confirmMessage = isSelf
      ? "⚠️ Are you sure you want to remove yourself from this company? You will lose access immediately."
      : `Are you sure you want to remove this user?`;

    if (window.confirm(confirmMessage)) {
      try {
        console.log("🗑️ DELETE REQUEST:", {
          userId,
          userRole,
          endpoint: `/api/users/${userId}`,
        });

        const response = await api.delete(`/api/users/${userId}`);

        console.log("✅ DELETE RESPONSE:", response.data);

        if (response.data.success) {
          showSuccess(response.data.message || "User removed successfully");
          fetchUsers();

          // If user deleted themselves, redirect to login
          if (isSelf) {
            setTimeout(() => {
              window.location.href = "/login";
            }, 1500);
          }
        } else {
          console.warn("⚠️ DELETE returned success=false:", response.data);
          showError(
            "Delete failed: " + (response.data.message || "Unknown error"),
          );
        }
      } catch (error) {
        console.error("❌ DELETE ERROR:", {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          endpoint: `/api/users/${userId}`,
        });
        const errorMessage =
          error.response?.data?.message ||
          `Failed to remove user (${error.response?.status || "Network Error"})`;
        showError(errorMessage);
      }
    }
  };

  // Handle edit user - open modal
  const handleEdit = (targetUser) => {
    if (!canEditUser(currentUserRole, targetUser.role)) {
      showError(
        isSellerManager
          ? "Seller Managers cannot edit Admin, Owner, or Editor users"
          : "You do not have permission to modify this user",
      );
      return;
    }
    setEditingUser(targetUser);
    setEditForm({
      firstName: targetUser.firstName || "",
      lastName: targetUser.lastName || "",
      role: targetUser.role || "",
      phoneNumber: targetUser.phoneNumber || "",
      storeName: targetUser.storeName || "",
      address: targetUser.address || "",
      skPrefix: targetUser.skPrefix || "",
      payoutPercentage: targetUser.payoutPercentage || "",
    });
    setEditModal(true);
  };

  // Handle role change
  const handleUpdateUser = async () => {
    if (!editingUser) return;
    if (
      editForm.role === "Seller" &&
      (!editForm.phoneNumber ||
        !editForm.storeName ||
        !editForm.address ||
        !editForm.skPrefix)
    ) {
      showWarning(
        "Phone Number, Store Name, Address and SKU Prefix are required for Seller",
      );
      return;
    }

    if (!canEditUser(currentUserRole, editingUser.role)) {
      showError("You do not have permission to modify this user");
      return;
    }

    if (!editForm.firstName.trim() || !editForm.lastName.trim()) {
      showWarning("First name and last name cannot be empty");
      return;
    }

    if (!canAddUserWithRole(currentUserRole, editForm.role)) {
      showError(
        isSellerManager
          ? "Seller Managers can only assign the Seller role"
          : "You do not have permission to assign this role",
      );
      return;
    }

    try {
      const response = await api.put(`/api/users/${editingUser.id}`, {
        firstName: editForm.firstName.trim(),
        lastName: editForm.lastName.trim(),
        role: editForm.role,
        phoneNumber: editForm.phoneNumber,
        storeName: editForm.storeName,
        address: editForm.address,
        skPrefix: editForm.skPrefix,
        payoutPercentage: editForm.payoutPercentage,
      });

      if (response.data.success) {
        if (response.data.multipleOwners) {
          showInfo("⚠️ " + response.data.message);
        } else {
          showSuccess(response.data.message || "User updated successfully");
        }
        setEditModal(false);
        setEditingUser(null);
        fetchUsers();
      } else {
        showError(
          "Update failed: " + (response.data.message || "Unknown error"),
        );
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        `Failed to update user (${error.response?.status || "Network Error"})`;
      showError(errorMessage);
    }
  };

  const handleLoginAs = async (targetUser) => {
    if (
      !canLoginAsUser(
        currentUserRole,
        targetUser.role,
        currentUserId,
        targetUser.id,
      )
    ) {
      showError(
        isSellerManager
          ? "Seller Managers can only login as Seller accounts"
          : "You do not have permission to login as this user",
      );
      return;
    }

    const displayName = `${targetUser.firstName} ${targetUser.lastName}`.trim();
    if (
      !window.confirm(
        `Open a new tab as ${displayName || targetUser.email}? This tab will stay logged in as you.`,
      )
    ) {
      return;
    }

    try {
      const response = await api.post(`/api/users/${targetUser.id}/login-as`);

      if (
        !response.data?.success ||
        !response.data?.token ||
        !response.data?.user
      ) {
        throw new Error(response.data?.message || "Failed to login as user");
      }

      const handoffKey = storeLoginAsHandoff(
        response.data.token,
        response.data.user,
      );
      const loginAsUrl = `${window.location.origin}/login-as?key=${encodeURIComponent(handoffKey)}`;
      const newTab = window.open(loginAsUrl, "_blank", "noopener,noreferrer");

      if (!newTab) {
        showWarning(
          "Pop-up blocked. Please allow pop-ups for this site and try again.",
        );
        return;
      }

      showSuccess(
        `Opened ${displayName || targetUser.email} in a new tab. This tab remains your admin session.`,
      );
    } catch (error) {
      console.error("Login as user error:", error);
      showError(
        error.response?.data?.message ||
          error.message ||
          "Failed to login as user",
      );
    }
  };

  return (
    <div className="users-page">
      <div className="page-header-section">
        <div>
          <h1 className="page-title" style={{ color: "#000000" }}>
            Users Management
          </h1>
          <p className="page-description">
            Manage team members and their access levels
          </p>
        </div>
        {userCanManageUsers && (
          <button className="add-user-btn" onClick={() => setShowModal(true)}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="8.5" cy="7" r="4" />
              <line x1="20" y1="8" x2="20" y2="14" />
              <line x1="23" y1="11" x2="17" y2="11" />
            </svg>
            <span className="add-user-btn-text">Add User</span>
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <p>Loading users...</p>
        </div>
      ) : (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Store Name</th>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((targetUser) => {
                const canEdit = canEditUser(currentUserRole, targetUser.role);
                const canDelete = canDeleteUser(
                  currentUserRole,
                  targetUser.role,
                );
                const canLogin = canLoginAsUser(
                  currentUserRole,
                  targetUser.role,
                  currentUserId,
                  targetUser.id,
                );

                return (
                  <tr key={targetUser.id}>
                    <td>
                      {targetUser.storeName}
                    </td>
                    <td>
                      {targetUser.firstName} {targetUser.lastName}
                    </td>
                    <td>{targetUser.email}</td>
                    <td>
                      <span
                        className={`role-badge ${roleToCssClass(targetUser.role)}`}
                      >
                        {targetUser.role}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        {canLogin && (
                          <button
                            className="action-icon-btn login-btn"
                            onClick={() => handleLoginAs(targetUser)}
                            title="Login as this user"
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                              <polyline points="10 17 15 12 10 7" />
                              <line x1="15" y1="12" x2="3" y2="12" />
                            </svg>
                          </button>
                        )}
                        {canEdit && (
                          <button
                            className="action-icon-btn edit-btn"
                            onClick={() => handleEdit(targetUser)}
                            title="Change role"
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                        )}
                        {canDelete && (
                          <button
                            className="action-icon-btn delete-btn"
                            onClick={() =>
                              handleDelete(targetUser.id, targetUser.role)
                            }
                            title="Remove user"
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {users.length === 0 && (
            <div className="empty-state">
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <p>No users found</p>
              {userCanManageUsers && (
                <button
                  className="add-user-btn-small"
                  onClick={() => setShowModal(true)}
                >
                  Add Your First User
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add User Modal */}
      <MDBModal open={showModal} staticBackdrop tabIndex="-1">
        <MDBModalDialog centered>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{ marginRight: "8px", verticalAlign: "middle" }}
                >
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="8.5" cy="7" r="4" />
                  <line x1="20" y1="8" x2="20" y2="14" />
                  <line x1="23" y1="11" x2="17" y2="11" />
                </svg>
                Add New User
              </MDBModalTitle>
            </MDBModalHeader>

            <MDBModalBody>
              <MDBInput
                label="First Name"
                name="firstName"
                size="lg"
                className="mb-3"
                value={formData.firstName}
                onChange={handleChange}
              />

              <MDBInput
                label="Last Name"
                name="lastName"
                size="lg"
                className="mb-3"
                value={formData.lastName}
                onChange={handleChange}
              />

              <MDBInput
                label="Email Address"
                name="email"
                type="email"
                size="lg"
                className="mb-3"
                value={formData.email}
                onChange={handleChange}
              />

              <MDBInput
                label="Password"
                name="password"
                type="password"
                size="lg"
                className="mb-3"
                value={formData.password}
                onChange={handleChange}
              />

              <select
                name="role"
                className="form-control form-control-lg mb-3"
                value={formData.role}
                onChange={handleChange}
              >
                <option value="">Select Role</option>
                {addUserRoleOptions.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>

              {formData.role === "Seller" && (
                <>
                  <MDBInput
                    label="Phone Number"
                    name="phoneNumber"
                    size="lg"
                    className="mb-3"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                  />

                  <MDBInput
                    label="Store Name"
                    name="storeName"
                    size="lg"
                    className="mb-3"
                    value={formData.storeName}
                    onChange={handleChange}
                  />

                  <MDBInput
                    label="Address"
                    name="address"
                    size="lg"
                    className="mb-3"
                    value={formData.address}
                    onChange={handleChange}
                  />
                  <div className="mb-3">
                    <MDBInput
                      label="SKU Prefix (e.g. OV for Oliver Vintage)"
                      name="skPrefix"
                      size="lg"
                      value={formData.skPrefix}
                      onChange={handleChange}
                      maxLength={5}
                    />
                    <small className="text-muted">
                      Suggested:{" "}
                      {formData.storeName
                        ? formData.storeName
                            .split(" ")
                            .map((w) => w[0]?.toUpperCase() || "")
                            .join("")
                            .slice(0, 4)
                        : "Enter store name first"}
                    </small>
                  </div>

                  <MDBInput
                    label="Payout Percentage (e.g. 70 for 70%)"
                    name="payoutPercentage"
                    type="number"
                    size="lg"
                    className="mb-3"
                    value={formData.payoutPercentage}
                    onChange={handleChange}
                    min={0}
                    max={100}
                  />
                </>
              )}
            </MDBModalBody>

            <MDBModalFooter>
              <MDBBtn
                color="secondary"
                onClick={() => {
                  setShowModal(false);
                  setFormData({
                    firstName: "",
                    lastName: "",
                    email: "",
                    password: "",
                    role: "",
                  });
                }}
              >
                Cancel
              </MDBBtn>
              <MDBBtn color="danger" onClick={handleSubmit}>
                Add User
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      {/* Edit Role Modal */}
      <MDBModal open={editModal} staticBackdrop tabIndex="-1">
        <MDBModalDialog centered>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>Change User Role</MDBModalTitle>
            </MDBModalHeader>

            <MDBModalBody>
              {editingUser && (
                <>
                  <p style={{ marginBottom: "16px" }}>
                    Editing{" "}
                    <strong>
                      {editingUser.firstName} {editingUser.lastName}
                    </strong>
                  </p>

                  <MDBInput
                    label="First Name"
                    size="lg"
                    className="mb-3"
                    value={editForm.firstName}
                    onChange={(e) =>
                      setEditForm({ ...editForm, firstName: e.target.value })
                    }
                  />

                  <MDBInput
                    label="Last Name"
                    size="lg"
                    className="mb-3"
                    value={editForm.lastName}
                    onChange={(e) =>
                      setEditForm({ ...editForm, lastName: e.target.value })
                    }
                  />

                  <p
                    style={{
                      marginBottom: "8px",
                      fontSize: "14px",
                      color: "#666",
                    }}
                  >
                    Current role:{" "}
                    <span
                      className={`role-badge ${roleToCssClass(editingUser.role)}`}
                    >
                      {editingUser.role}
                    </span>
                  </p>
                  {editForm.role === "Seller" && (
                    <>
                      <MDBInput
                        label="Phone Number"
                        size="lg"
                        className="mb-3"
                        value={editForm.phoneNumber}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            phoneNumber: e.target.value,
                          })
                        }
                      />

                      <MDBInput
                        label="Store Name"
                        size="lg"
                        className="mb-3"
                        value={editForm.storeName}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            storeName: e.target.value,
                          })
                        }
                      />

                      <MDBInput
                        label="Address"
                        size="lg"
                        className="mb-3"
                        value={editForm.address}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            address: e.target.value,
                          })
                        }
                      />

                      <MDBInput
                        label="SKU Prefix"
                        size="lg"
                        className="mb-3"
                        value={editForm.skPrefix}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            skPrefix: e.target.value,
                          })
                        }
                      />

                      <MDBInput
                        label="Payout Percentage (e.g. 70 for 70%)"
                        type="number"
                        size="lg"
                        className="mb-3"
                        value={editForm.payoutPercentage}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            payoutPercentage: e.target.value,
                          })
                        }
                        min={0}
                        max={100}
                      />
                    </>
                  )}

                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      marginTop: "16px",
                      flexDirection: "column",
                    }}
                  >
                    {editUserRoleOptions.map((role) => (
                      <button
                        key={role.value}
                        onClick={() =>
                          setEditForm({ ...editForm, role: role.value })
                        }
                        style={{
                          padding: "12px",
                          border: `2px solid ${editForm.role === role.value ? role.borderColor : "#dee2e6"}`,
                          background:
                            editForm.role === role.value
                              ? `${role.borderColor}15`
                              : "white",
                          borderRadius: "8px",
                          cursor: "pointer",
                          fontWeight: "500",
                          textAlign: "left",
                          transition: "all 0.15s ease",
                        }}
                      >
                        {role.icon} {role.label}{" "}
                        <span style={{ fontSize: "13px", color: "#666" }}>
                          — {role.description}
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </MDBModalBody>

            <MDBModalFooter>
              <MDBBtn
                color="secondary"
                onClick={() => {
                  setEditModal(false);
                  setEditingUser(null);
                }}
              >
                Cancel
              </MDBBtn>
              <MDBBtn color="danger" onClick={handleUpdateUser}>
                Save Changes
              </MDBBtn>
            </MDBModalFooter>

            <MDBModalFooter>
              <MDBBtn
                color="secondary"
                onClick={() => {
                  setEditModal(false);
                  setEditingUser(null);
                }}
              >
                Cancel
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      <MessageBoxComponent />
    </div>
  );
};

export default InviteUsersPage;
